import Vue from "vue";
import Vuex from "vuex";
import App from "./App.vue";
import RouterConfigurer from "./router";
import { RouteConfig } from "vue-router";

import { BootstrapVue } from "bootstrap-vue";
import axios from "axios";
import VueAxios from "vue-axios";
import "ant-design-vue/dist/antd.css";
import "./scss/boostrap-config.scss";
import { authService } from "./auth";
import { ProtectedResource } from "./protectedresource";
import { Config } from "./config";
import { ChatService as VideoChat } from "./videochat";
import { ChatService as TextChat, registerChatStateModule } from "./chat";
import { WebSocketManager } from "./websocket";
import { log } from "@/logging";
import { S3Utilities } from "@/s3utilities";
import { AnalyticsService } from "@/analytics";
import { ValueType, Value } from "./analytics/model/analyticsEvent";
import { AnalyticsContext } from "./analytics/analyticscontext";
import { NotificationService } from "@/notification";
import { v4 as uuidv4 } from "uuid";
import { ZendeskManager } from "@/zendesk";
import { LifeCycleUtilities } from "./browserutilities";
import { LanguageService } from "./language";

const myLoggingName = "Main";

//import { registerChatStateModule } from "./components/chat/KingEventsChat.vue";

Vue.use(Vuex);
Vue.config.productionTip = false;

// https://markus.oberlehner.net/blog/the-ioc-container-pattern-with-vue

let routerConfigurer = new RouterConfigurer();
let router = routerConfigurer.createRouter([]);
let videoChatSocketManager: WebSocketManager | null = null;
let textChatSocketManager: WebSocketManager | null = null;
let analytics: AnalyticsService | null = null;

//TODO have the router populate this for each route. The Contexts will manage state, e.g. whether egress should happen or not.
// So the logic below that adds and removes claims checks can go, just call egress an ingress.
let roomAnalyticsContexts: Map<string, AnalyticsContext> = new Map<string, AnalyticsContext>();
let zendeskManager: ZendeskManager | null = null;

let documentState = window.document.visibilityState;
//let currentAnalyitcsContext : AnalyticsContext | null = null;
const appSessionId = uuidv4();

let lifecycle = new LifeCycleUtilities();
const appLocation = "/appnavbar";
const contextLocation = new Array<string>();
contextLocation.push(appLocation);
//const appContext = new AnalyticsContext(analytics!!, appSessionId, contextLocation);
let appContext = new AnalyticsContext(analytics!!, appSessionId, contextLocation);
let modeKey: String | null = null;
let languageService: LanguageService | null = null;

fetch(process.env.BASE_URL + "config.json")
	.then(response => {
		return response.json();
	})
	.then(async config => {
		let host = window.location.hostname;
		let path = window.location.pathname.split("/");
		const hash = document.location?.hash.substring(1);
		log(myLoggingName, "appLoad pre hash check", { hash: hash, path: path });
		//		if (!hash || hash === "/") {
		if (path.length == 2) {
			modeKey = path[1];
		} else {
			modeKey = "";
		}
		//		}
		let configService = new Config(axios, config, host, modeKey);

		const protectedResource = new ProtectedResource(axios, configService);
		// A centralized client of the videochat service that can be shared amongst components, e.g. so video chat functionality can be accessed from multiple "rooms"
		videoChatSocketManager = new WebSocketManager("VideoChat");
		textChatSocketManager = new WebSocketManager("TextChat");
		const videoChatService = new VideoChat(authService, configService, videoChatSocketManager);
		const textChatService = new TextChat(authService, textChatSocketManager);
		const vuexStore = new Vuex.Store({});
		const s3Utilities = new S3Utilities(configService);
		analytics = new AnalyticsService(axios, configService);
		appContext = new AnalyticsContext(analytics!!, appSessionId, contextLocation);
		zendeskManager = new ZendeskManager("concierge-zendesk", "ze-snippet", false);
		let notificationService = new NotificationService(axios, configService);
		languageService = new LanguageService(vuexStore);

		registerChatStateModule(vuexStore, textChatService);

		new Vue({
			el: "#app",
			router,
			store: vuexStore,
			render: h =>
				h(App, {
					props: {
						analyticsContext: appContext
					}
				}),
			provide: {
				authService: authService,
				protectedResource,
				configService,
				videoChatService: videoChatService,
				textChatService: textChatService,
				s3Utilities: s3Utilities,
				zendeskManager: zendeskManager,
				notificationService: notificationService,
				languageService: languageService
			}
		});
		// example of how to provide props to top level Vue
		// new Vue({
		// 	el: "#app",
		// 	router,
		// 	render: h =>
		// 		h(App, {
		// 			props: {
		// 				common: siteConfig.common
		// 			}
		// 		}),
		// 	provide: { authService: authService, protectedResource, configService }
		// });
		// And how to NOT provide props
		// new Vue({
		// 	el: "#app",
		// 	router,
		// 	render: h =>
		// 		h(App),
		// 	provide: { authService: authService, protectedResource, configService }
		// });
		return configService;
	})
	.then(async configService => {
		let siteConfig = await configService.getSiteConfig();
		zendeskManager!.init(siteConfig["concierge"].script);
		languageService!.init(siteConfig);

		videoChatSocketManager!.url = siteConfig["videoChatConfig"]?.videoChatUrl;

		// If the mode Key was specified, will redirect to default page for that mode
		let activeMode = configService.determineActiveSiteMode(siteConfig);
		let mode = configService.getActiveSiteMode(siteConfig, activeMode.mode);
		let gaurdedPageList = configService.getGuardedPageNames(siteConfig);

		let authConfig = await configService.getAuth();
		authService.init(authConfig, mode.isAnonymousOverride);
		const authed = await authService.auth();

		const routes = routerConfigurer.createRoutes(siteConfig, authService, mode, gaurdedPageList) as RouteConfig[];
		router.addRoutes(routes);
		roomAnalyticsContexts = addAnalyticContextsToRoutes(analytics!!, appSessionId, routes);
		roomAnalyticsContexts.set(appLocation, appContext);

		const hash = document.location?.hash.substring(1);
		let initialRoute = hash ?? "/";
		const initialPage = mode.defaultPage;
		log(myLoggingName, "appLoad", {
			documentURI: document.documentURI,
			hash: hash,
			effectiveTo: hash,
			currentState: documentState,
			mode: mode,
			pageNames: siteConfig.pages.map((it: any) => it.routeName),
			initialRoute,
			initialPage
		});

		// If a mode key was supplied
		// if initialRoute provided, will route to that location (whether valid or not - will let the router handle route validation as the logic is complex)
		// else route to default for the mode
		if (modeKey !== null) {
			if (initialRoute != "/") {
				log(myLoggingName, "appLoad Routing Setup reroute for mode override with supplied hash", {
					mode: mode,
					initialPage: initialPage,
					initialRoute: initialRoute
				});
			} else {
				initialRoute = siteConfig.pages.find((it: any) => it.routeName === initialPage).routePath;
				log(myLoggingName, "appLoad Routing Setup reroute for mode override", {
					mode: mode,
					initialPage: initialPage,
					initialRoute: initialRoute
				});
			}
		}
		log(myLoggingName, "appLoad Routing", { initialRoute: initialRoute });
		if (analytics) {
			// Set up a background task to sends chunks of analytics to the service
			const analyticsConfig = await analytics.getConfig();
			const location = initialRoute;
			setInterval(() => analytics!.flush(), analyticsConfig.flushInterval);
			let defaultContext = roomAnalyticsContexts.get(location);
			if (!defaultContext) {
				log(myLoggingName, "appLoad", "Add default context");

				const contextLocation = new Array<string>();
				contextLocation.push(location);
				defaultContext = new AnalyticsContext(analytics!!, appSessionId, contextLocation);
				roomAnalyticsContexts.set(location, defaultContext);
			}
			roomAnalyticsContexts.set(appLocation, appContext);

			try {
				const contextValues = new Map<string, Value>();
				let value: Value = {
					type: ValueType.String,
					value: window.navigator.userAgent
				};
				contextValues.set("userAgent", value);
				value = {
					type: ValueType.String,
					value: "[" + window.navigator.languages.join(",") + "]"
				};
				contextValues.set("languages", value);

				const query = new URLSearchParams(window.location.search).entries();
				let queryparms = Array<string>();
				for (var pair of query) {
					queryparms.push(pair[0] + ': "' + pair[1] + '"');
				}
				value = {
					type: ValueType.String,
					value: "{" + queryparms.join(",") + "}"
				};
				contextValues.set("query", value);

				const authenticationPageConfig = siteConfig.pages.filter((page: any) => {
					return page.type === "authentication";
				});
				const captureAttributesInAnalytics = authenticationPageConfig[0]?.config?.analytics?.captureAttributes;

				if (authed && captureAttributesInAnalytics && captureAttributesInAnalytics.length > 0 && authService.user) {
					captureAttributesInAnalytics.forEach((key: string) => {
						value = {
							type: ValueType.String,
							value: (authService.user!! as any).attributes[key]
						};
						contextValues.set("userAttribute" + key, value);
					});
				}
				// I think it is OK to let the promise run instead of convert to async/await - Not sure how to how async/await with router
				defaultContext.ingress(contextValues);
			} catch (err) {
				log(myLoggingName, "appLoad", "analytics capture error", err);
			}

			await analytics!.flush();
		}

		// dimensions
		// host: string; - not necessary - determined server side
		// userId: string; - not necessary - determined server side from token
		// region: string; - unnecessary
		// ip: string; -- client doesn't know this, lambda to provide
		// location: string; - event room
		// session: string; - application session - regenerated each page load
		// values:
		// event: string; - eg start, end, error
		// duration: string (number)
		// locationSession: string
		router.beforeEach((to, from, next) => {
			try {
				const hash = document.location?.hash.substring(1);
				log(myLoggingName, "routerBeforeEach", "AnalyticsEgress", {
					routerTo: to.fullPath,
					routerFrom: from.fullPath,
					documentURI: document.documentURI,
					hash: hash,
					effectiveFrom: hash,
					claimCheckLocation: roomAnalyticsContexts.get(from.fullPath)!!.location
				});
				let context = roomAnalyticsContexts.get(from.fullPath);
				if (context) {
					log(myLoggingName, "routerBeforeEach", "Egress", {
						routerTo: to.fullPath,
						routerFrom: from.fullPath,
						documentURI: document.documentURI,
						hash: hash,
						effectiveFrom: hash,
						context: context.session
					});
					// I think it is OK to let the promise run instead of convert to async/await - Not sure how to how async/await with router
					context.egress("navigation");
				} else {
					log(myLoggingName, "routerBeforeEach", "Egress error, context not found");
				}
				log(myLoggingName, "routerBeforeEach", "AnalyticsIngress", {
					to: to.fullPath,
					from: from.fullPath,
					documentURI: document.documentURI,
					hash: hash,
					effectiveTo: to.fullPath
				});
				context = roomAnalyticsContexts.get(to.fullPath);
				if (context) {
					// I think it is OK to let the promise run instead of convert to async/await - Not sure how to how async/await with router
					context.ingress(new Map<string, Value>());
				} else {
					log(myLoggingName, "routerBeforeEach", "Ingress error, context not found");
				}
				//recordIngress(appSessionId, to.fullPath);
			} catch (error) {
				log(myLoggingName, "routerBeforeEach", "Analytics", "error", error);
			}
			next();
		});

		// https://developers.google.com/web/updates/2018/07/page-lifecycle-api

		lifecycle.addListener(async (event: any) => {
			const hash = document.location?.hash.substring(1);
			let actionText = "no stage change";
			let action = "none";
			try {
				switch (event.newState) {
					case "hidden":
						action = "egress";
						actionText = "egress from" + hash;
						break;
					case "active":
					case "passive":
						if (event.oldState !== "active" && event.oldState != "passive") {
							action = "ingress";
							actionText = "ingress to" + hash;
						}
						break;
					default:
						break;
				}
				log(myLoggingName, "lifecycle", {
					visibilityState: document.visibilityState,
					eventNewState: event.newState,
					eventOldState: event.oldState,
					cause: event.originalEvent,
					hash: hash,
					currentState: documentState,
					action: action,
					actionText: actionText
				});
				switch (action) {
					case "egress":
						await handleHidden("pageLifeCycleHandler", "hidden", hash);
						break;

					case "ingress":
						await handleUnhidden("pageLifeCycleHandler:unhidden", hash);
						//await recordIngress(appSessionId, hash);
						break;
					default:
						break;
				}
			} catch (error) {
				log(myLoggingName, "pageLifeCycleHandler", "Analytics", "error", error);
			} finally {
				documentState = event.newState;
			}
		});

		log(myLoggingName, "appLoad Routing Pre-Redirect", { modeKey: modeKey, initialRoute: initialRoute, hash: hash });

		if (initialRoute != hash) {
			//			if (modeKey != null && initialRoute != hash) {
			log(myLoggingName, "appLoad Routing Redirecting", { initialRoute: initialRoute });
			router.replace(initialRoute);
		}
	});

// For each route:
// -  create an analyticsContext
// - add it to the props of the route
// - add it to a map, keyed by route full path
// - return the map
//
// Goal - make the analyitcsContext available to the view in the route so that it can add children and record events
// - return a map of "rooms" to contexts so that page lifecycle and room enter and leave handlers can access the contexts
function addAnalyticContextsToRoutes(
	analyticsService: AnalyticsService,
	appSessionId: string,
	routes: any[]
): Map<string, AnalyticsContext> {
	const contexts = new Map<string, AnalyticsContext>();
	routes.forEach(it => {
		const location = it.path;
		const contextLocation = new Array<string>();
		contextLocation.push(location);
		let newContext = new AnalyticsContext(analytics!!, appSessionId, contextLocation);
		contexts.set(location, newContext);
		const props = it.props as any;
		// log(myLoggingName, "addAnalyticContextsToRoutes", "debug", {
		// 	routePath: location
		// });
		try {
			if (props) {
				props.analyticsContext = newContext;
			}
		} catch (err) {
			log(myLoggingName, "addAnalyticContextsToRoutes", "err", {
				routePath: location,
				route: it,
				props: props,
				err: err
			});
		}
	});

	return contexts;
}

// TODO hide and unhide all?

async function handleUnhidden(reason: string, hash: string) {
	//	let context = roomAnalyticsContexts.get(hash);
	log(myLoggingName, "handleUnHidden", {
		visibilityState: document.visibilityState,
		documentURI: document.documentURI,
		hash: hash,
		currentState: documentState,
		reason: reason
	});

	for (const context of roomAnalyticsContexts) {
		await context[1].waken(reason);
	}
}

async function handleHidden(reason: string, newState: any, hash: string) {
	try {
		log(myLoggingName, "handleHidden", {
			visibilityState: document.visibilityState,
			newState: newState,
			documentURI: document.documentURI,
			hash: hash,
			currentState: documentState,
			action: "egress from " + hash,
			isStateChange: newState != documentState,
			reason: reason
		});
		for (const context of roomAnalyticsContexts) {
			await context[1].hibernate("hibernate");
		}
	} finally {
		await analytics?.flush();
	}
}

Vue.use(VueAxios, axios);
Vue.use(BootstrapVue);
